/**
 * useCompetenceFocusSession — "practise THIS competence, now".
 *
 * Starts a conversation session pre-targeted on one grammar competence: the
 * slug rides session creation as `focusGrammarPoint` and becomes the
 * Learner-Chosen Focus block in the tutor's first prompt
 * (competence-mastery.api.md § CM-020). The grammar counterpart of the Sound
 * Map's "practice this sound".
 *
 * Extracted from the Practice tab's recommendation card rather than written
 * inline there, because the entry point is the part most likely to multiply — a
 * competence map, the due-review queue and the in-chat feedback card all want
 * the same "tap the weakness, land in a session about it" move, and each one
 * re-deriving the consent gate is how the gate goes missing on the third.
 *
 * Consent is checked BEFORE the request: POST /api/conversation/sessions is
 * `consent: required`, so an ungated tap spends a round-trip to earn a 403 and
 * shows the learner nothing. Same gate, same modal, same ordering as the
 * Conversation Hub's mode cards.
 *
 * @see docs/specs/mobile/practice.mobile.md
 * @see docs/specs/api/competence-mastery.api.md
 */

import { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';

import { useAIConsent } from './useAIConsent';

/**
 * The conversation store is loaded lazily, NOT imported at module scope.
 *
 * A static import would put it in the module graph of every screen that renders
 * a focus button — and it drags the whole socket → auth → syncQueue → apiClient
 * chain behind it. The Practice tab would be loading the socket client in order
 * to draw a card. `require` rather than `import()` because Jest runs this tree
 * in CJS mode, the same reason `sessionSlice` loads `userPreferencesStore` this
 * way.
 *
 * Nothing is lost by not subscribing: `startNewSession` is a store action with a
 * stable identity, it is only ever called from a callback, and the session id is
 * read imperatively straight after.
 */
function conversationStore(): typeof import('../store/conversationStore').useConversationStore {
  const { useConversationStore } =
    require('../store/conversationStore') as typeof import('../store/conversationStore');
  return useConversationStore;
}

/**
 * The flow a competence focus opens in.
 *
 * `conversation`, not `grammar`: the focus block tells the tutor to steer the
 * talk toward situations that REQUIRE the structure so the learner produces it
 * — which is the conversation flow's job. The grammar flow explains and drills,
 * and a drill needs no steering hint.
 */
const FOCUS_FLOW_TYPE = 'conversation';

export interface CompetenceFocusSession {
  /**
   * Start a focused session for `slug` and navigate to Chat. No-op while
   * another start is in flight. Opens the consent modal instead of requesting
   * when consent is missing, and resumes on accept.
   */
  startFocusedSession: (slug: string) => Promise<void>;
  /** The competence currently being started, or null. Drives the row spinner. */
  startingSlug: string | null;
  /** Whether the AI-consent modal should be shown. */
  consentModalVisible: boolean;
  /** Grant consent, then start the session the learner originally tapped. */
  acceptConsent: () => Promise<void>;
  /** Dismiss the modal and drop the pending competence. */
  declineConsent: () => void;
}

export function useCompetenceFocusSession(): CompetenceFocusSession {
  const navigation = useNavigation();
  const { hasConsent, needsReConsent, grantConsent } = useAIConsent();

  const [startingSlug, setStartingSlug] = useState<string | null>(null);
  const [consentModalVisible, setConsentModalVisible] = useState(false);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  const createAndNavigate = useCallback(
    async (slug: string) => {
      setStartingSlug(slug);
      try {
        const store = conversationStore();
        await store.getState().startNewSession(FOCUS_FLOW_TYPE, {
          focusGrammarPoint: slug,
        });
        // Read through the store rather than a return value: `startNewSession`
        // reports failure by leaving `currentSessionId` null and setting
        // `errorInfo`, so a null here means "don't navigate", not "no id yet".
        const sessionId = store.getState().currentSessionId;
        if (sessionId) {
          navigation.navigate('Chat', { sessionId });
        }
      } finally {
        setStartingSlug(null);
      }
    },
    [navigation],
  );

  const startFocusedSession = useCallback(
    async (slug: string) => {
      if (startingSlug !== null) return;

      if (!hasConsent || needsReConsent) {
        setPendingSlug(slug);
        setConsentModalVisible(true);
        return;
      }

      await createAndNavigate(slug);
    },
    [startingSlug, hasConsent, needsReConsent, createAndNavigate],
  );

  const acceptConsent = useCallback(async () => {
    const granted = await grantConsent();
    // Dismiss unconditionally: the AppBanner that `useAIConsent` raises on
    // failure renders at navigator root and would sit behind this native Modal.
    setConsentModalVisible(false);
    const slug = pendingSlug;
    setPendingSlug(null);

    // On failure the pending competence is dropped deliberately — retrying
    // would POST, earn AI_CONSENT_REQUIRED, and re-open this modal over the
    // banner the learner has not read yet. Re-tapping the row is the retry.
    if (!granted || !slug) return;

    await createAndNavigate(slug);
  }, [grantConsent, pendingSlug, createAndNavigate]);

  const declineConsent = useCallback(() => {
    setConsentModalVisible(false);
    setPendingSlug(null);
  }, []);

  return {
    startFocusedSession,
    startingSlug,
    consentModalVisible,
    acceptConsent,
    declineConsent,
  };
}
